import type { Budget, versionInfo } from "../types/budget/budget";
import type { ServiceWriteTotalsAttachment } from "../types/budget/budget-totals";
import { backfillMissingCategoryFiscalRows } from "./backfill-missing-base-values";
import { applyServerTotalsSlices } from "./budget-server-totals";
import { getPhaseValuesFromService } from "./budget-service-writes";
import { resolveStageTotalsTree } from "./visible-stage-totals";

const FALLBACK_VERSION_INFO: versionInfo = {
  comparisonVersion: 0,
  version: 0,
  status: "OPEN",
};

/**
 * Version context for hydrate-time server totals refresh (FE-P4-01).
 * Prefer the UI's open version when it exists on the document; otherwise
 * version 0 (orçamento) / first history entry.
 */
export function resolveHydrateVersionInfo(
  budget: Budget,
  preferred?: versionInfo | null
): versionInfo {
  if (
    preferred &&
    typeof preferred.version === "number" &&
    budget.versionHistory?.some((entry) => entry.version === preferred.version)
  ) {
    return preferred;
  }

  return (
    budget.versionHistory?.find((entry) => entry.version === 0) ||
    budget.versionHistory?.[0] ||
    FALLBACK_VERSION_INFO
  );
}

/**
 * Etapa cujos totais o recálculo da abertura deve reconstruir.
 *
 * O histórico guarda `DENIED` / `DENIED_CLOSING`, mas o valor continua no
 * bloco da negociação / do fechamento. Sem este mapa o rebuild cai no
 * orçamento (v0) e a tela de Fechamento abre zerada.
 */
export function versionInfoForTotalsRebuild(info: versionInfo): versionInfo {
  if (info.status === "DENIED_CLOSING") {
    return { ...info, status: "APPROVED" };
  }
  if (info.status === "DENIED" && (info.version ?? 0) > 0) {
    return { ...info, status: "NEGOTIATE" };
  }
  return info;
}

/**
 * Apply a `GET /v2/budget/:id/totals` (or write-response) attachment onto a
 * hydrated editor document so list/summary snapshots cannot keep a stale tree.
 */
export function applyHydrateServerTotals(
  budget: Budget,
  attachment:
    | Pick<ServiceWriteTotalsAttachment, "totals" | "totalsVersion">
    | undefined
): Budget {
  if (!attachment?.totals?.length) return budget;
  return applyServerTotalsSlices(budget, attachment);
}

/**
 * Ordem do hydrate: completa o perfil fiscal da categoria que falta
 * (`backfillMissingCategoryFiscalRows`) e SÓ ENTÃO recalcula. Invertido, o
 * recálculo lê o `baseValue` zerado do fallback e zera Taxa MM/Imposto das
 * linhas. `beforeRebuild` é o documento já completado — é contra ele que
 * `hasHydrateRebuildDrift` compara.
 */
export function backfillThenRebuildHydratedBudget(
  budget: Budget,
  rebuild: (budget: Budget) => Budget
): { beforeRebuild: Budget; rebuilt: Budget } {
  const beforeRebuild = backfillMissingCategoryFiscalRows(budget);
  return { beforeRebuild, rebuilt: rebuild(beforeRebuild) };
}

/** Diferença abaixo de meio centavo é arredondamento, não divergência. */
const DRIFT_TOLERANCE_BRL = 0.005;

function differs(a: number | undefined, b: number | undefined): boolean {
  return Math.abs((a ?? 0) - (b ?? 0)) > DRIFT_TOLERANCE_BRL;
}

/**
 * MMV-2626-3: o recálculo feito ao abrir o orçamento (`RECALCULATE_TOTALS` em
 * `rebuildTotalsWithTaxOverlay`) mudou o que está gravado?
 *
 * Esse recálculo refaz as linhas e a árvore da etapa só em memória, e entra
 * no editor como carga da API (não conta como alteração). Quando o gravado
 * estava defasado — árvore montada por regra antiga, totais que não chegaram
 * a ir no autosave —, a tela mostrava o valor novo enquanto a lista de
 * orçamentos e o xlsx liam o velho, e o "salvar antes de exportar" não
 * gravava nada. Compara a árvore da etapa e o valor de cada linha na etapa.
 */
export function hasHydrateRebuildDrift(
  loaded: Budget,
  rebuilt: Budget,
  versionInfo: Pick<versionInfo, "version" | "status">
): boolean {
  const stageSum = (budget: Budget) =>
    resolveStageTotalsTree({
      budgetValuesTotal: budget.budgetValuesTotal,
      negociationValuesTotal: budget.negociationValuesTotal,
      closingValuesTotal: budget.closingValuesTotal,
      status: versionInfo.status,
      version: versionInfo.version,
    })?.byItemType?.SUM?.totalInBRL;

  if (differs(stageSum(loaded), stageSum(rebuilt))) return true;

  const loadedById = new Map(
    (loaded.budgetServices ?? []).map((service) => [service._id, service])
  );
  return (rebuilt.budgetServices ?? []).some((service) => {
    const before = loadedById.get(service._id);
    if (!before) return false;
    return differs(
      getPhaseValuesFromService(before, versionInfo)?.totals?.totalInBRL,
      getPhaseValuesFromService(service, versionInfo)?.totals?.totalInBRL
    );
  });
}
